import * as React from "react";
import { useNaverMaps } from "./useNaverMaps";
import type { BBox, Coord } from "./types";

interface Props {
  // 백엔드 /api/v1/map/points 응답의 center / zoom / bbox를 그대로 받음.
  center: Coord;
  zoom?: number;
  bbox?: BBox | null;

  // 줌 한계 (네이버 SDK의 minZoom/maxZoom). 권역 단위 진입은 12~16 정도가 자연스럽다.
  minZoom?: number;
  maxZoom?: number;

  // 조작 가능 여부. 기본 true(풀 지도). false면 드래그/줌/키보드/컨트롤을 모두
  // 잠가 "보기 전용" 지도가 된다 — 대시보드 미니맵처럼 표시만 하는 표면에서 사용.
  // 마커 클릭은 지도 조작과 독립이라 false여도 그대로 동작한다.
  interactive?: boolean;

  // bbox fit 시 컨테이너 가장자리에서 띄울 여백(px, 4면 동일). 작은 카드형 지도는
  // 60px 기본값이 과해 점이 가운데로 몰리므로 미니맵은 더 작은 값을 준다.
  fitPadding?: number;

  // fitBounds 후 적용할 최소 줌. 가로로 납작한 미니맵 카드에서 좁은 지역 클러스터를
  // fit하면 위도 span을 짧은 높이에 맞추느라 과도하게 줌아웃돼(해안 도시면 바다가
  // 크게 노출) "지도 같지 않게" 보인다. 이 값이 주어지면 그 아래로는 내려가지 않게
  // 줌을 끌어올린다. 미지정이면 기존 동작(순수 fitBounds) 그대로.
  minFitZoom?: number;

  // 컨테이너 스타일. MapView의 grid 셀 안에 들어가므로 부모가 height를 책임진다.
  style?: React.CSSProperties;

  // 지도 위에 absolute로 얹을 자식 (검색박스, 스케일바, 토글 등).
  // NaverMapCanvas는 position:relative 컨테이너를 만들고, children은 그 위에 떠 있게 한다.
  children?: React.ReactNode;

  // 지도 인스턴스 노출. 단계 3+에서 부모가 마커/오버레이를 그릴 때 사용.
  // useEffect dep로 안전하게 쓰려면 setMap 자체는 동일 참조여야 함 → useCallback 권장.
  onMapReady?: (map: any) => void;
}

// 권역 지도의 컨테이너 + 네이버 SDK Map 인스턴스 라이프사이클을 책임지는 컴포넌트.
// 단계 2 범위: 빈 지도 + bbox fit + loading/error 폴백만. 마커/오버레이는 단계 3-4.
export function NaverMapCanvas({
  center,
  zoom = 12,
  bbox,
  minZoom = 6,
  maxZoom = 19,
  interactive = true,
  fitPadding = 60,
  minFitZoom,
  style,
  children,
  onMapReady,
}: Props) {
  const sdkState = useNaverMaps();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<any>(null);
  const fittedOnceRef = React.useRef(false);

  // 지도 인스턴스 생성은 SDK ready 직후 단 1회. center/zoom 변화로는 재생성하지 않는다
  // (사용자가 줌/팬 한 뒤 props 변화가 들어와도 시점이 깨지지 않도록).
  React.useEffect(() => {
    if (sdkState !== "ready") return;
    if (!containerRef.current || mapRef.current) return;

    const naver = window.naver!;
    // 보기 전용 모드: 모든 사용자 조작 제스처를 잠근다. 마커 click 리스너는
    // 지도 드래그/줌과 무관하게 계속 발화하므로 별도 처리가 필요 없다.
    const lockOpts = interactive ? {} : {
      draggable: false,
      pinchZoom: false,
      scrollWheel: false,
      keyboardShortcuts: false,
      disableDoubleClickZoom: true,
      disableDoubleTapZoom: true,
      disableTwoFingerTapZoom: true,
    };
    const map = new naver.maps.Map(containerRef.current, {
      center: new naver.maps.LatLng(center.lat, center.lng),
      zoom,
      minZoom,
      maxZoom,
      zoomControl: interactive,
      zoomControlOptions: { position: naver.maps.Position.TOP_RIGHT },
      mapTypeControl: false,
      scaleControl: interactive,
      ...lockOpts,
    });
    mapRef.current = map;
    onMapReady?.(map);

    return () => {
      try { map.destroy?.(); } catch { /* SDK 버전별 차이 흡수 */ }
      mapRef.current = null;
      fittedOnceRef.current = false;
    };
    // center/zoom/minZoom/maxZoom/onMapReady 변화는 의도적으로 무시한다 (재생성 방지).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkState]);

  // bbox로 자동 fit — 최초 1회만. 이후 사용자 줌/팬 결과를 덮어쓰지 않는다.
  // 한 점만 있는 권역(north==south)은 fit이 의미 없으므로 건너뛴다.
  React.useEffect(() => {
    if (sdkState !== "ready" || !mapRef.current || !bbox) return;
    if (fittedOnceRef.current) return;
    if (bbox.north === bbox.south && bbox.east === bbox.west) return;

    const naver = window.naver!;
    const bounds = new naver.maps.LatLngBounds(
      new naver.maps.LatLng(bbox.south, bbox.west),
      new naver.maps.LatLng(bbox.north, bbox.east),
    );
    mapRef.current.fitBounds(bounds, { top: fitPadding, right: fitPadding, bottom: fitPadding, left: fitPadding });
    // 납작한(가로로 긴) 미니맵 컨테이너에서는 위도 span을 짧은 높이에 맞추느라
    // fitBounds가 과도하게 줌아웃해(해안 도시면 바다까지 노출) "지도 같지 않게"
    // 보인다. minFitZoom이 주어지면 그 아래로는 내려가지 않게 끌어올린다(중심 유지).
    // 가로 여백(점이 없는 영역)은 조금 잘려도 클러스터가 또렷한 지역 지도가 된다.
    if (minFitZoom != null && mapRef.current.getZoom?.() < minFitZoom) {
      mapRef.current.setZoom(minFitZoom, true);
    }
    fittedOnceRef.current = true;
  }, [sdkState, bbox, fitPadding, minFitZoom]);

  // 컨테이너 크기가 바뀌면 네이버 지도에 'resize'를 통지해 새 영역을 다시 타일링한다.
  // 지도 인스턴스는 생성 시점의 컨테이너 크기로 타일 그리드를 잡는데, 이후 컨테이너가
  // 커지면(모달 열림·사이드바 토글·반응형 reflow·지연 마운트 등) SDK는 그걸 모른 채
  // 옛 그리드를 유지한다 → 늘어난 영역이 회색으로 남고, 확대하면 그 회색이 더 두드러진다.
  // ResizeObserver로 크기 변화를 감지해 relayout한다(초기 1회 발화로 "생성 시 크기가
  // 덜 잡힌" 경우도 함께 보정). center는 resize가 흔들 수 있어 통지 후 복원한다.
  React.useEffect(() => {
    if (sdkState !== "ready") return;
    const el = containerRef.current;
    const naver = window.naver;
    if (!el || !naver?.maps || typeof ResizeObserver === "undefined") return;

    let raf = 0;
    const relayout = () => {
      const map = mapRef.current;
      if (!map) return;
      const center = map.getCenter?.();
      naver.maps.Event.trigger(map, "resize");
      if (center) map.setCenter(center);
    };
    const ro = new ResizeObserver(() => {
      // 연속 리사이즈를 1프레임으로 합쳐 레이아웃 확정 후 한 번만 통지.
      window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(relayout);
    });
    ro.observe(el);
    return () => { window.cancelAnimationFrame(raf); ro.disconnect(); };
  }, [sdkState]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%", ...style }}>
      {/* 지도 컨테이너 — 배경색은 시안과 일치(타일 로딩 사이 깜빡임 완화). */}
      <div
        ref={containerRef}
        style={{ position: "absolute", inset: 0, background: "#E7E1CF" }}
      />

      {/* 부모가 얹는 검색박스 / 스케일바 / 토글 등. */}
      {children}

      {sdkState === "loading" && (
        <div style={OVERLAY_STYLE}>
          <div className="muted" style={{
            background: "rgba(255,255,255,0.92)", padding: "8px 14px",
            borderRadius: 8, fontSize: 12, fontWeight: 600,
          }}>
            지도를 불러오는 중…
          </div>
        </div>
      )}

      {sdkState === "error" && (
        <div style={OVERLAY_STYLE}>
          <div style={{
            background: "rgba(255,255,255,0.96)", padding: "16px 20px",
            borderRadius: 10, maxWidth: 360, textAlign: "center",
            boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              네이버 지도 SDK를 불러오지 못했습니다
            </div>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.55 }}>
              관리자에게 문의해주세요. NCP Maps의 도메인 허용 목록 또는 키 주입을
              확인해야 합니다.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center",
  pointerEvents: "none",
};
